import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { TenantSubscription } from '../../../../types/subscriptions';
import { useCancelSubscription, useResumeSubscription } from '../../../../api/subscriptions';
import { useHasPermission } from '../../../../contexts/auth';
import CustomModal from '../../../../components/ui/modal/modal.component';
import { formatDate } from '../../../../utils';
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from '../../../../consts';

interface SubscriptionCancellationProps {
  subscription: TenantSubscription;
}

export const SubscriptionCancellation: React.FC<SubscriptionCancellationProps> = ({
  subscription,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canManage = useHasPermission('payment.add_tenantsubscription');
  const { cancel, loading: cancelling } = useCancelSubscription();
  const { resume, loading: resuming } = useResumeSubscription();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!canManage) {
    return null;
  }

  const afterChange = (ok: boolean, okMessage: string, failMessage: string) => {
    if (ok) {
      window.dispatchEvent(new Event('tenantUpdated'));
      enqueueSnackbar(okMessage, SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(failMessage, ERROR_SNACKBAR);
    }
  };

  const handleCancel = async () => {
    const { success } = await cancel();
    setConfirmOpen(false);
    afterChange(
      success,
      t('Subscription cancellation scheduled.'),
      t('Could not cancel the subscription. Please try again.')
    );
  };

  const handleResume = async () => {
    const { success } = await resume();
    afterChange(
      success,
      t('Cancellation aborted. Your subscription continues.'),
      t('Could not abort the cancellation. Please try again.')
    );
  };

  if (subscription.cancellation_pending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="info"
          size="small"
          onClick={handleResume} disabled={resuming}
        >
          {t('Abort cancellation')}
        </Button>
      </Box>
    );
  }

  if (!subscription.can_cancel) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => setConfirmOpen(true)}
        >
          {t('Cancel subscription')}
        </Button>
      </Box>

      <CustomModal
        opened={confirmOpen}
        setOpened={setConfirmOpen}
        title={t('Cancel subscription')}
        width="min(440px, 92vw)"
        minWidth="min(440px, 92vw)"
        content={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t(
                'The plan stays fully usable until {{date}}, the end of the current paid period. After that the workspace is locked until a new plan is chosen. No further charges are made.',
                { date: formatDate(subscription.paid_until) }
              )}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
              <Button onClick={() => setConfirmOpen(false)}>{t('Keep subscription')}</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? t('Processing...') : t('Confirm cancellation')}
              </Button>
            </Box>
          </Box>
        }
      />
    </>
  );
};
